import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { createCompany } from '../services/api';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Rocket, User, Building2, FolderOpen, CreditCard, 
  CheckCircle2, ArrowRight, ArrowLeft, Plus, X, Download, Monitor
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';

const OnboardingPage = () => {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const { user, completeOnboarding } = useAuth();
  const navigate = useNavigate();

  // Step 1: Profile
  const [phone, setPhone] = useState('');

  // Step 2: Companies
  const [companies, setCompanies] = useState([{
    name: '',
    legal_form: '',
    address: '',
    industry: '',
    vat_id: ''
  }]);

  // Step 3: Document sources (placeholder for now)
  const [hotFolderEnabled, setHotFolderEnabled] = useState(false);
  const [emailEnabled, setEmailEnabled] = useState(false);

  // Step 4: Payment (Stripe placeholder)
  const [stripeConnected, setStripeConnected] = useState(false);

  const totalSteps = 5;

  const industries = [
    'IT & Software',
    'Marketing & Agentur',
    'Beratung & Consulting',
    'Design & Kreativ',
    'Handwerk',
    'Gastronomie',
    'Einzelhandel',
    'Gesundheit',
    'Bildung',
    'Sonstiges'
  ];

  const legalForms = [
    'Einzelunternehmen',
    'GbR',
    'UG (haftungsbeschränkt)',
    'GmbH',
    'GmbH & Co. KG',
    'AG',
    'Freiberufler',
    'Sonstiges'
  ];

  const addCompany = () => {
    setCompanies([...companies, { name: '', legal_form: '', address: '', industry: '', vat_id: '' }]);
  };

  const removeCompany = (index) => {
    if (companies.length > 1) {
      setCompanies(companies.filter((_, i) => i !== index));
    }
  };

  const updateCompany = (index, field, value) => {
    const updated = [...companies];
    updated[index][field] = value;
    setCompanies(updated);
  };

  const handleNext = async () => {
    if (step === 2) {
      // Create companies
      const validCompanies = companies.filter(c => c.name.trim());
      if (validCompanies.length === 0) {
        toast.error('Bitte mindestens ein Unternehmen anlegen');
        return;
      }
      
      setLoading(true);
      try {
        for (const company of validCompanies) {
          await createCompany(company);
        }
        toast.success('Unternehmen erfolgreich angelegt!');
      } catch (error) {
        toast.error('Fehler beim Anlegen der Unternehmen');
        setLoading(false);
        return;
      }
      setLoading(false);
    }
    
    if (step < totalSteps) {
      setStep(step + 1);
    }
  };

  const handleFinish = async () => {
    setLoading(true);
    try {
      await completeOnboarding();
      toast.success('Willkommen bei Invoice OS!');
      navigate('/dashboard');
    } catch (error) {
      toast.error('Fehler beim Abschließen');
    } finally {
      setLoading(false);
    }
  };

  const stepVariants = {
    enter: { opacity: 0, x: 20 },
    center: { opacity: 1, x: 0 },
    exit: { opacity: 0, x: -20 }
  };

  return (
    <div className="min-h-screen bg-[#F2F2F7] flex flex-col">
      {/* Header */}
      <div className="bg-white/80 backdrop-blur-xl border-b border-gray-100/50 py-4 px-6">
        <div className="max-w-3xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-indigo-500 to-violet-500 rounded-xl flex items-center justify-center shadow-lg shadow-indigo-500/25">
              <Rocket className="w-5 h-5 text-white" />
            </div>
            <span className="font-semibold text-lg" style={{ fontFamily: 'Outfit, sans-serif' }}>
              JaySmart
            </span>
          </div>
          <p className="text-sm text-gray-500">
            Schritt {step} von {totalSteps}
          </p>
        </div>
      </div>

      {/* Progress bar */}
      <div className="bg-white/50 px-6">
        <div className="max-w-3xl mx-auto py-4">
          <div className="h-1 bg-gray-200 rounded-full overflow-hidden">
            <motion.div 
              className="h-full bg-gradient-to-r from-indigo-500 to-violet-500"
              initial={{ width: 0 }}
              animate={{ width: `${(step / totalSteps) * 100}%` }}
              transition={{ duration: 0.3 }}
            />
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 px-6 py-12">
        <div className="max-w-2xl mx-auto">
          <AnimatePresence mode="wait">
            {/* Step 1: Profile */}
            {step === 1 && (
              <motion.div
                key="step1"
                variants={stepVariants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={{ duration: 0.3 }}
              >
                <div className="w-16 h-16 bg-indigo-100 rounded-2xl flex items-center justify-center mb-6">
                  <User className="w-8 h-8 text-indigo-600" />
                </div>
                <h2 className="text-3xl font-bold mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Willkommen, {user?.name || 'Nutzer'}!
                </h2>
                <p className="text-gray-600 mb-8">
                  Lass uns dein Konto einrichten. Das dauert nur 2 Minuten.
                </p>

                <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
                  <div className="space-y-4">
                    <div>
                      <Label className="text-sm font-medium text-gray-700">Name</Label>
                      <Input
                        value={user?.name || ''}
                        disabled
                        className="mt-2 h-12 rounded-xl bg-gray-50 border-gray-200"
                      />
                    </div>
                    <div>
                      <Label className="text-sm font-medium text-gray-700">E-Mail</Label>
                      <Input
                        value={user?.email || ''}
                        disabled
                        className="mt-2 h-12 rounded-xl bg-gray-50 border-gray-200"
                      />
                    </div>
                    <div>
                      <Label className="text-sm font-medium text-gray-700">
                        Telefonnummer (optional)
                      </Label>
                      <Input
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                        placeholder="+49 123 456789"
                        className="mt-2 h-12 rounded-xl bg-white border-gray-200 focus:border-indigo-500"
                        data-testid="onboarding-phone-input"
                      />
                    </div>
                  </div>
                </div>
              </motion.div>
            )}

            {/* Step 2: Companies */}
            {step === 2 && (
              <motion.div
                key="step2"
                variants={stepVariants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={{ duration: 0.3 }}
              >
                <div className="w-16 h-16 bg-indigo-100 rounded-2xl flex items-center justify-center mb-6">
                  <Building2 className="w-8 h-8 text-indigo-600" />
                </div>
                <h2 className="text-3xl font-bold mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Deine Unternehmen
                </h2>
                <p className="text-gray-600 mb-8">
                  Füge dein(e) Unternehmen hinzu. Du kannst auch mehrere anlegen.
                </p>

                <div className="space-y-4">
                  {companies.map((company, index) => (
                    <div key={index} className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="font-semibold" style={{ fontFamily: 'Outfit, sans-serif' }}>
                          Unternehmen {index + 1}
                        </h3>
                        {companies.length > 1 && (
                          <button
                            onClick={() => removeCompany(index)}
                            className="text-gray-400 hover:text-red-500 transition-colors"
                          >
                            <X className="w-5 h-5" />
                          </button>
                        )}
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="col-span-2">
                          <Label className="text-sm font-medium text-gray-700">Firmenname *</Label>
                          <Input
                            value={company.name}
                            onChange={(e) => updateCompany(index, 'name', e.target.value)}
                            placeholder="Meine Firma GmbH"
                            className="mt-2 h-12 rounded-xl bg-white border-gray-200 focus:border-indigo-500"
                            data-testid={`company-name-input-${index}`}
                          />
                        </div>
                        <div>
                          <Label className="text-sm font-medium text-gray-700">Rechtsform</Label>
                          <Select
                            value={company.legal_form}
                            onValueChange={(value) => updateCompany(index, 'legal_form', value)}
                          >
                            <SelectTrigger className="mt-2 h-12 rounded-xl bg-white border-gray-200">
                              <SelectValue placeholder="Auswählen..." />
                            </SelectTrigger>
                            <SelectContent>
                              {legalForms.map((form) => (
                                <SelectItem key={form} value={form}>{form}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label className="text-sm font-medium text-gray-700">Branche</Label>
                          <Select
                            value={company.industry}
                            onValueChange={(value) => updateCompany(index, 'industry', value)}
                          >
                            <SelectTrigger className="mt-2 h-12 rounded-xl bg-white border-gray-200">
                              <SelectValue placeholder="Auswählen..." />
                            </SelectTrigger>
                            <SelectContent>
                              {industries.map((ind) => (
                                <SelectItem key={ind} value={ind}>{ind}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="col-span-2">
                          <Label className="text-sm font-medium text-gray-700">Adresse</Label>
                          <Input
                            value={company.address}
                            onChange={(e) => updateCompany(index, 'address', e.target.value)}
                            placeholder="Musterstraße 1, 12345 Berlin"
                            className="mt-2 h-12 rounded-xl bg-white border-gray-200 focus:border-indigo-500"
                          />
                        </div>
                        <div className="col-span-2">
                          <Label className="text-sm font-medium text-gray-700">USt-ID (optional)</Label>
                          <Input
                            value={company.vat_id}
                            onChange={(e) => updateCompany(index, 'vat_id', e.target.value)}
                            placeholder="DE123456789"
                            className="mt-2 h-12 rounded-xl bg-white border-gray-200 focus:border-indigo-500"
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                <button
                  onClick={addCompany}
                  className="mt-4 w-full flex items-center justify-center gap-2 p-4 border-2 border-dashed border-gray-200 rounded-2xl text-gray-500 hover:border-indigo-500 hover:text-indigo-600 transition-colors"
                  data-testid="add-company-btn"
                >
                  <Plus className="w-5 h-5" />
                  Weiteres Unternehmen hinzufügen
                </button>
              </motion.div>
            )}

            {/* Step 3: Document Sources */}
            {step === 3 && (
              <motion.div
                key="step3"
                variants={stepVariants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={{ duration: 0.3 }}
              >
                <div className="w-16 h-16 bg-indigo-100 rounded-2xl flex items-center justify-center mb-6">
                  <FolderOpen className="w-8 h-8 text-indigo-600" />
                </div>
                <h2 className="text-3xl font-bold mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Dokument-Quellen
                </h2>
                <p className="text-gray-600 mb-8">
                  Wie möchtest du Dokumente importieren? (Kann später geändert werden)
                </p>

                <div className="space-y-4">
                  {/* Desktop Sync Client */}
                  <div className={`w-full p-6 rounded-2xl border-2 text-left transition-colors ${
                    hotFolderEnabled 
                      ? 'border-indigo-500 bg-indigo-50' 
                      : 'border-gray-200 bg-white'
                  }`}>
                    <div className="flex items-start gap-4">
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                        hotFolderEnabled ? 'bg-indigo-500' : 'bg-gray-100'
                      }`}>
                        <Monitor className={`w-6 h-6 ${hotFolderEnabled ? 'text-white' : 'text-gray-500'}`} />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-semibold mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>
                          Desktop Sync Client
                        </h3>
                        <p className="text-sm text-gray-600 mb-3">
                          Synchronisiere Dokumente automatisch zwischen deinem Computer und JaySmart. 
                          Neue Dateien werden erkannt, hochgeladen und von der KI analysiert.
                        </p>
                        <div className="flex items-center gap-3">
                          <Button
                            onClick={() => {
                              setHotFolderEnabled(true);
                              window.open(`${process.env.REACT_APP_BACKEND_URL}/api/download/sync-client`, '_blank');
                            }}
                            className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-indigo-500 to-violet-500 text-white text-sm font-medium rounded-lg hover:from-indigo-600 hover:to-violet-600 shadow-md"
                            data-testid="download-sync-client-btn"
                          >
                            <Download className="w-4 h-4" />
                            Sync Client herunterladen
                          </Button>
                          <span className="text-xs text-gray-500">Windows, macOS, Linux</span>
                        </div>
                      </div>
                      <button
                        onClick={() => setHotFolderEnabled(!hotFolderEnabled)}
                        className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                          hotFolderEnabled ? 'border-indigo-500 bg-indigo-500' : 'border-gray-300'
                        }`}
                        data-testid="hot-folder-toggle"
                      >
                        {hotFolderEnabled && <CheckCircle2 className="w-4 h-4 text-white" />}
                      </button>
                    </div>
                  </div>

                  <button
                    onClick={() => setEmailEnabled(!emailEnabled)}
                    className={`w-full p-6 rounded-2xl border-2 text-left transition-colors ${
                      emailEnabled 
                        ? 'border-indigo-500 bg-indigo-50' 
                        : 'border-gray-200 bg-white hover:border-gray-300'
                    }`}
                    data-testid="email-import-toggle"
                  >
                    <div className="flex items-start gap-4">
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                        emailEnabled ? 'bg-indigo-500' : 'bg-gray-100'
                      }`}>
                        <Rocket className={`w-6 h-6 ${emailEnabled ? 'text-white' : 'text-gray-500'}`} />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-semibold mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>
                          E-Mail-Postfach verbinden
                        </h3>
                        <p className="text-sm text-gray-600">
                          Verbinde ein Postfach (z.B. rechnungen@deine-domain.de) und 
                          lass Anhänge automatisch importieren.
                        </p>
                        <p className="text-xs text-amber-600 mt-2">
                          ⚠️ E-Mail-Import ist in der Demo-Version simuliert.
                        </p>
                      </div>
                      <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                        emailEnabled ? 'border-indigo-500 bg-indigo-500' : 'border-gray-300'
                      }`}>
                        {emailEnabled && <CheckCircle2 className="w-4 h-4 text-white" />}
                      </div>
                    </div>
                  </button>
                </div>
              </motion.div>
            )}

            {/* Step 4: Payment */}
            {step === 4 && (
              <motion.div
                key="step4"
                variants={stepVariants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={{ duration: 0.3 }}
              >
                <div className="w-16 h-16 bg-indigo-100 rounded-2xl flex items-center justify-center mb-6">
                  <CreditCard className="w-8 h-8 text-indigo-600" />
                </div>
                <h2 className="text-3xl font-bold mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Zahlungen
                </h2>
                <p className="text-gray-600 mb-8">
                  Verbinde Stripe, um Rechnungen direkt bezahlen zu können. (Optional)
                </p>

                <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-[#635BFF] rounded-xl flex items-center justify-center">
                      <span className="text-white font-bold text-lg">S</span>
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>
                        Stripe Integration
                      </h3>
                      <p className="text-sm text-gray-600 mb-4">
                        Mit Stripe kannst du Rechnungen direkt aus der App bezahlen. 
                        Sicher und verschlüsselt.
                      </p>
                      <div className="flex items-center gap-2 text-sm text-green-600 bg-green-50 px-3 py-2 rounded-lg inline-flex">
                        <CheckCircle2 className="w-4 h-4" />
                        Stripe ist bereits konfiguriert (Test-Modus)
                      </div>
                    </div>
                  </div>
                </div>

                <p className="mt-4 text-sm text-gray-500">
                  Du kannst die Stripe-Integration jederzeit in den Einstellungen ändern.
                </p>
              </motion.div>
            )}

            {/* Step 5: Finish */}
            {step === 5 && (
              <motion.div
                key="step5"
                variants={stepVariants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={{ duration: 0.3 }}
                className="text-center"
              >
                <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <CheckCircle2 className="w-10 h-10 text-green-600" />
                </div>
                <h2 className="text-3xl font-bold mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Alles bereit!
                </h2>
                <p className="text-gray-600 mb-8 max-w-md mx-auto">
                  Dein Konto ist eingerichtet. Du kannst jetzt mit dem Hochladen 
                  deiner ersten Dokumente beginnen.
                </p>

                <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 text-left max-w-md mx-auto">
                  <h4 className="font-semibold mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    Nächste Schritte:
                  </h4>
                  <ul className="space-y-3">
                    <li className="flex items-center gap-3 text-sm">
                      <span className="w-6 h-6 bg-[#0055FF]/10 rounded-full flex items-center justify-center text-[#0055FF] font-medium">1</span>
                      Lade dein erstes Dokument hoch
                    </li>
                    <li className="flex items-center gap-3 text-sm">
                      <span className="w-6 h-6 bg-[#0055FF]/10 rounded-full flex items-center justify-center text-[#0055FF] font-medium">2</span>
                      Überprüfe die KI-Extraktion
                    </li>
                    <li className="flex items-center gap-3 text-sm">
                      <span className="w-6 h-6 bg-[#0055FF]/10 rounded-full flex items-center justify-center text-[#0055FF] font-medium">3</span>
                      Verschiebe Dokumente im Kanban-Board
                    </li>
                  </ul>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* Footer Navigation */}
      <div className="bg-white border-t border-gray-100 py-4 px-6">
        <div className="max-w-2xl mx-auto flex items-center justify-between">
          {step > 1 ? (
            <Button
              variant="ghost"
              onClick={() => setStep(step - 1)}
              className="rounded-full"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Zurück
            </Button>
          ) : (
            <div />
          )}
          
          {step < totalSteps ? (
            <Button
              onClick={handleNext}
              disabled={loading}
              className="bg-gradient-to-r from-indigo-500 to-violet-500 hover:from-indigo-600 hover:to-violet-600 text-white rounded-full px-8 shadow-lg shadow-indigo-500/25"
              data-testid="onboarding-next-btn"
            >
              {loading ? 'Laden...' : 'Weiter'}
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          ) : (
            <Button
              onClick={handleFinish}
              disabled={loading}
              className="bg-gradient-to-r from-indigo-500 to-violet-500 hover:from-indigo-600 hover:to-violet-600 text-white rounded-full px-8 shadow-lg shadow-indigo-500/25"
              data-testid="onboarding-finish-btn"
            >
              {loading ? 'Laden...' : 'Zum Dashboard'}
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default OnboardingPage;
